import {inject, Injectable} from '@angular/core';
import type {BootstrapAccess, BootstrapResult, BootstrapUserData, BootstrapUserPlanData} from '@app/core/interfaces';
import {BehaviorSubject, Observable, ReplaySubject} from 'rxjs';

import {PrefixStoreService} from '@app/core/store/prefix-store.service';

import {DeviceType} from "../models/device-type";
import {SidebarModeType} from "../models/sidebar-mode";
import {HelpModeType} from '@shared/components/help/models/help-mode.type';
import type {UserResult} from '@app/main/modules/user-account/api/user-api.models';
import type {WorkspaceResult} from '@app/main/modules/teammates/api/workspace-api.models';


@Injectable({
  providedIn: 'root'
})
export class StoreService {
  private readonly prefixStore = inject(PrefixStoreService);

  hasUpdateSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  hasUpdate$: Observable<boolean> = this.hasUpdateSubject.asObservable();

  private userInfo: BehaviorSubject<UserResult | null> = new BehaviorSubject<UserResult | null>(null)
  userInfo$: Observable<UserResult | null> = this.userInfo.asObservable();

  private deviceMode: ReplaySubject<DeviceType> = new ReplaySubject<DeviceType>(1)
  deviceMode$: Observable<DeviceType> = this.deviceMode.asObservable();

  private sidebarMode: ReplaySubject<SidebarModeType> = new ReplaySubject<SidebarModeType>(1)
  sidebarMode$: Observable<SidebarModeType> = this.sidebarMode.asObservable();

  private helpMode: BehaviorSubject<HelpModeType> = new BehaviorSubject<HelpModeType>('closed')
  helpMode$: Observable<HelpModeType> = this.helpMode.asObservable();

  private baseData: BehaviorSubject<BootstrapResult | null> = new BehaviorSubject<BootstrapResult | null>(null)
  baseData$: Observable<BootstrapResult | null> = this.baseData.asObservable();

  private currentWorkspace: BehaviorSubject<WorkspaceResult | null> = new BehaviorSubject<WorkspaceResult | null>(null)
  currentWorkspace$: Observable<WorkspaceResult | null> = this.currentWorkspace.asObservable();


  setBaseData(payload: BootstrapResult | null) {
    this.baseData.next(payload)
  }

  getBaseData(): BootstrapResult | null {
    return this.baseData.getValue();
  }

  setCurrentWorkspace(payload: WorkspaceResult | null) {
    this.currentWorkspace.next(payload);
  }

  getCurrentWorkspace(): WorkspaceResult | null {
    return this.currentWorkspace.getValue();
  }

  setHelpMode(payload: HelpModeType) {
    this.helpMode.next(payload)
  }

  setDeviceMode(payload: DeviceType) {
    this.deviceMode.next(payload)
  }

  setHasUpdate(payload: boolean) {
    this.hasUpdateSubject.next(payload)
  }

  initSidebarMode(deviceMode: DeviceType) {
    switch (deviceMode) {
      case 'mobile':
        this.sidebarMode.next('hidden');
        break;
      case 'tablet':
        this.sidebarMode.next('hidden');
        break;
      case 'desktop':
        this.sidebarMode.next('default');
        break;
      default:
        this.sidebarMode.next('default');
    }
  }

  modifySidebar(mode: SidebarModeType) {
    this.sidebarMode.next(mode);
  }

  getPrefixes(operators?: string[]): string[] {
    return this.prefixStore.getPrefixes(operators);
  }

  confirmAlert(alertId: number) {
    let data = this.baseData.getValue();
    if (data) {
      data = {
        ...data,
        userData: {
          ...data.userData,
          confirmation: (data.userData.confirmation ?? 0) - Number(alertId)
        }
      }
    }
    this.baseData.next(data)
  }

  getUserInfoSuccess(payload: UserResult) {
    let userInfo = this.userInfo.getValue();
    userInfo = {
      ...userInfo,
      ...payload
    }
    this.userInfo.next(userInfo);
  }

  avatarUpdatedSuccess(newUrl: string | null) {
    let baseData = this.baseData.getValue();
    let userData = this.userInfo.getValue();
    if (baseData) {
      baseData = {
        ...baseData,
        userData: {
          ...baseData.userData,
          profileImageURL: newUrl
        }
      }
    }
    if (userData) {
      userData = {
        ...userData,
        profileImageURL: newUrl
      }
    }
    this.baseData.next(baseData);
    this.userInfo.next(userData);
  }

  emailUpdatedSuccess(payload: any) {
    let userData = this.userInfo.getValue();
    if (userData) {
      userData = {
        ...userData,
        email: payload.email
      }
    }
    this.userInfo.next(userData);
  }


  setUserVerificationView(payload: { isVerified: boolean, verificationCelebrationViewed: boolean }) {
    let data = this.baseData.getValue();
    if (data) {
      data = {
        ...data,
        userData: {
          ...data.userData,
          isVerified: payload.isVerified,
          verificationCelebrationViewed: payload.verificationCelebrationViewed,
        }
      }
    }
    this.baseData.next(data)
  }

  setUserAccesses(payload: BootstrapAccess[]) {
    let data = this.baseData.getValue();
    if (data) {
      data = {
        ...data,
        userData: {
          ...data.userData,
          accesses: payload
        }
      };
      this.baseData.next(data);
    }
  }

  setUserData(payload: BootstrapUserData) {
    let data: BootstrapResult | null = this.baseData.getValue();
    if (data) {
      data = {
        ...data,
        userData: payload
      };
      this.baseData.next(data);
    }
  }

  setUserIsLegal(isLegal: boolean) {
    const data = this.baseData.getValue();
    if (!data || data.userData.isLegal === isLegal) {
      return;
    }

    this.baseData.next({
      ...data,
      userData: {
        ...data.userData,
        isLegal,
      },
    });
  }

  setUserPlanData(payload: BootstrapUserPlanData) {
    let data: BootstrapResult | null = this.baseData.getValue();
    if (data) {
      data = {
        ...data,
        userPlanData: payload
      };
      this.baseData.next(data);
    }
  }
}
